/**
 * WorkspacePhasePermissions - Aba de permissões granulares por fase de Negócio (Workspace)
 *
 * Renderizada dentro do expand de cada workspace em CrmWorkspaces.tsx.
 *
 * Comportamento:
 *  - Switch principal: liga/desliga `phase_permissions_enabled` no workspace.
 *  - Quando OFF: exibe banner explicando que a regra padrão está ativa.
 *  - Quando ON:
 *      • Banner de aviso explicando que supervisores também passam a respeitar a matriz.
 *      • Seletor de Perfil de Permissão (lista perfis ativos do tenant).
 *      • Matriz [fase × ação] usando o componente compartilhado PhasePermissionsTable.
 *      • Botão Salvar (chama RPC `upsert_crm_phase_permissions`).
 *
 * Apenas administradores podem alterar (camada server-side via RLS + RPC checks).
 *
 * @version 1.1.0
 * RELEASE: 2026-04-23 v1.0.0 - Criação inicial
 * RELEASE: 2026-09-08 v1.1.0 - Layout compacto: switch/banners/seletor de perfil reduzidos;
 *          banners migrados para o padrão InlineNotice (barra lateral colorida, sem fundo).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Loader2, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import {
  PhasePermissionsTable,
  type PhasePermissionColumn,
  type PhasePermissionState,
} from '@/components/permissions/PhasePermissionsTable';

const logger = createLogger('WorkspacePhasePermissions');

/**
 * Colunas (ações) controladas no CRM — apenas as 6 solicitadas pelo cliente.
 */
const CRM_PERMISSION_COLUMNS: readonly PhasePermissionColumn[] = [
  { key: 'can_view', label: 'Ver', description: 'Pode visualizar negócios nesta fase' },
  { key: 'can_create', label: 'Criar', description: 'Pode criar negócios nesta fase' },
  { key: 'can_edit', label: 'Editar', description: 'Pode editar negócios nesta fase' },
  { key: 'can_delete', label: 'Excluir', description: 'Pode excluir negócios nesta fase' },
  { key: 'can_reopen', label: 'Reabrir', description: 'Pode reabrir negócios concluídos/perdidos nesta fase' },
  { key: 'can_change_phase', label: 'Mover Fase', description: 'Pode mover negócios para/desta fase' },
  // v1.1.0 - Ação de exceção: destrava os produtos do negócio ao cancelar uma proposta aceita
  { key: 'can_cancel_accepted_proposal', label: 'Cancelar Proposta Aceita', description: 'Pode cancelar uma proposta comercial já ACEITA de negócios nesta fase' },
] as const;

interface WorkspacePhasePermissionsProps {
  workspaceId: string;
  initialEnabled: boolean;
}

interface PermissionProfile {
  id: string;
  name: string;
}

interface PhaseFromRpc {
  phase_id: string;
  phase_name: string;
  phase_color: string;
  pipeline_id: string;
  pipeline_name: string;
  pipeline_sort_order: number;
  phase_sort_order: number;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_reopen: boolean;
  can_change_phase: boolean;
  can_cancel_accepted_proposal: boolean;
}

export function WorkspacePhasePermissions({ workspaceId, initialEnabled }: WorkspacePhasePermissionsProps) {
  const { currentTenant } = useAuth();
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();

  // Estado local do toggle (otimista)
  const [enabled, setEnabled] = useState(initialEnabled);
  const [togglingEnabled, setTogglingEnabled] = useState(false);

  // Sincroniza com prop quando muda (após refetch do parent)
  useEffect(() => {
    setEnabled(initialEnabled);
  }, [initialEnabled]);

  // Perfil selecionado para edição
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  // Estado em edição da matriz (phase_id → permissões)
  const [permissionsState, setPermissionsState] = useState<Map<string, PhasePermissionState>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // ==========================================================================
  // Query: lista de perfis de permissão ativos do tenant
  // ==========================================================================
  const { data: profiles = [], isLoading: loadingProfiles } = useQuery({
    queryKey: ['permission-profiles', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [] as PermissionProfile[];
      // Server-side: substitui SELECT direto em permission_profiles por RPC
      const { data, error } = await supabase.rpc('get_permission_profiles_list', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      return ((data || []) as any[])
        .filter(p => p.is_active)
        .map(p => ({ id: p.id, name: p.name })) as PermissionProfile[];
    },
    enabled: !!currentTenant?.id && enabled && isAdmin,
    staleTime: 60_000,
  });

  // ==========================================================================
  // Query: matriz atual do perfil selecionado (via RPC admin)
  // ==========================================================================
  const { data: phasesData, isLoading: loadingPhases, refetch: refetchPhases } = useQuery({
    queryKey: ['crm-phase-permissions-admin', workspaceId, selectedProfileId, currentTenant?.id],
    queryFn: async () => {
      if (!selectedProfileId || !currentTenant?.id) return [] as PhaseFromRpc[];
      const { data, error } = await supabase.rpc('get_crm_phase_permissions_admin', {
        p_workspace_id: workspaceId,
        p_profile_id: selectedProfileId,
        p_tenant_hint: currentTenant.id,
      } as never);
      if (error) throw error;
      return (data || []) as PhaseFromRpc[];
    },
    enabled: enabled && isAdmin && !!selectedProfileId && !!currentTenant?.id,
    staleTime: 30_000,
  });

  // Inicializa o estado local quando carrega os dados
  useEffect(() => {
    if (!phasesData) return;
    const map = new Map<string, PhasePermissionState>();
    phasesData.forEach((row) => {
      map.set(row.phase_id, {
        phase_id: row.phase_id,
        can_view: row.can_view,
        can_create: row.can_create,
        can_edit: row.can_edit,
        can_delete: row.can_delete,
        can_reopen: row.can_reopen,
        can_change_phase: row.can_change_phase,
        can_cancel_accepted_proposal: row.can_cancel_accepted_proposal,
      });
    });
    setPermissionsState(map);
    setHasChanges(false);
  }, [phasesData]);

  // ==========================================================================
  // Derivar fases ordenadas + grupos (pipelines) para o componente compartilhado
  // ==========================================================================
  const { phases, groups } = useMemo(() => {
    if (!phasesData) return { phases: [], groups: [] };

    // Mapa pipeline_id → grupo
    const groupMap = new Map<string, { id: string; name: string; color: string; sort: number }>();
    phasesData.forEach((row) => {
      if (!groupMap.has(row.pipeline_id)) {
        groupMap.set(row.pipeline_id, {
          id: row.pipeline_id,
          name: row.pipeline_name,
          color: 'hsl(var(--primary))',
          sort: row.pipeline_sort_order ?? 0,
        });
      }
    });

    const sortedGroups = Array.from(groupMap.values())
      .sort((a, b) => a.sort - b.sort)
      .map(({ sort: _sort, ...g }) => g);

    const sortedPhases = [...phasesData]
      .sort((a, b) => {
        if (a.pipeline_sort_order !== b.pipeline_sort_order) {
          return a.pipeline_sort_order - b.pipeline_sort_order;
        }
        return a.phase_sort_order - b.phase_sort_order;
      })
      .map((row) => ({
        id: row.phase_id,
        name: row.phase_name,
        color: row.phase_color || '#6B7280',
        group_id: row.pipeline_id,
      }));

    return { phases: sortedPhases, groups: sortedGroups };
  }, [phasesData]);

  // ==========================================================================
  // Handlers da matriz
  // ==========================================================================
  const togglePermission = useCallback((phaseId: string, columnKey: string) => {
    setPermissionsState((prev) => {
      const next = new Map(prev);
      const current = next.get(phaseId) ?? { phase_id: phaseId };
      next.set(phaseId, { ...current, [columnKey]: !(current[columnKey] ?? false) });
      return next;
    });
    setHasChanges(true);
  }, []);

  const toggleColumn = useCallback(
    (columnKey: string) => {
      setPermissionsState((prev) => {
        const next = new Map(prev);
        // Verifica se TODAS as fases estão marcadas para esta coluna
        let allChecked = phases.length > 0;
        for (const ph of phases) {
          if (!(next.get(ph.id)?.[columnKey] ?? false)) {
            allChecked = false;
            break;
          }
        }
        for (const ph of phases) {
          const current = next.get(ph.id) ?? { phase_id: ph.id };
          next.set(ph.id, { ...current, [columnKey]: !allChecked });
        }
        return next;
      });
      setHasChanges(true);
    },
    [phases]
  );

  const toggleRow = useCallback((phaseId: string) => {
    setPermissionsState((prev) => {
      const next = new Map(prev);
      const current = next.get(phaseId) ?? { phase_id: phaseId };
      const allChecked = CRM_PERMISSION_COLUMNS.every((c) => current[c.key] ?? false);
      const updated: PhasePermissionState = { phase_id: phaseId };
      CRM_PERMISSION_COLUMNS.forEach((c) => {
        updated[c.key] = !allChecked;
      });
      next.set(phaseId, updated);
      return next;
    });
    setHasChanges(true);
  }, []);

  // ==========================================================================
  // Mutations
  // ==========================================================================

  // Toggle do switch principal: atualiza phase_permissions_enabled no workspace
  const toggleEnabled = async (newValue: boolean) => {
    if (!currentTenant?.id) return;
    setTogglingEnabled(true);
    try {
      const { error } = await supabase
        .from('crm_workspaces')
        .update({ phase_permissions_enabled: newValue } as never)
        .eq('id', workspaceId)
        .eq('tenant_id', currentTenant.id);

      if (error) throw error;

      setEnabled(newValue);
      // Invalida queries dependentes
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-phase-permissions'] });

      toast.success(
        newValue
          ? 'Permissões granulares por fase ATIVADAS para este workspace'
          : 'Permissões granulares por fase DESATIVADAS — regra padrão restaurada'
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('Erro ao alternar phase_permissions_enabled', { error: msg });
      toast.error(`Erro ao alterar configuração: ${msg}`);
    } finally {
      setTogglingEnabled(false);
    }
  };

  // Mutation: salvar matriz via RPC upsert
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!currentTenant?.id || !selectedProfileId) throw new Error('Perfil não selecionado');

      // Monta o array com TODAS as fases (a RPC ignora linhas inválidas)
      const rows = phases.map((ph) => {
        const p = permissionsState.get(ph.id) ?? { phase_id: ph.id };
        return {
          phase_id: ph.id,
          can_view: p.can_view ?? false,
          can_create: p.can_create ?? false,
          can_edit: p.can_edit ?? false,
          can_delete: p.can_delete ?? false,
          can_reopen: p.can_reopen ?? false,
          can_change_phase: p.can_change_phase ?? false,
          can_cancel_accepted_proposal: p.can_cancel_accepted_proposal ?? false,
        };
      });

      const { error } = await supabase.rpc('upsert_crm_phase_permissions', {
        p_workspace_id: workspaceId,
        p_profile_id: selectedProfileId,
        p_rows: rows,
        p_tenant_hint: currentTenant.id,
      } as never);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Permissões salvas com sucesso');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['crm-phase-permissions'] });
      void refetchPhases();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('Erro ao salvar permissões CRM', { error: msg });
      toast.error(`Erro ao salvar: ${msg}`);
    },
  });

  // ==========================================================================
  // Renderização
  // ==========================================================================

  // Não-admin não vê esta aba (camada extra; já bloqueado server-side)
  if (!isAdmin) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">
          Apenas administradores podem visualizar e configurar permissões granulares por fase.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ==================== SWITCH PRINCIPAL ==================== */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border bg-muted/20">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
          <div className="min-w-0">
            <span className="text-sm font-medium">Habilitar permissões granulares por fase</span>
            <span className="hidden sm:inline text-xs text-muted-foreground"> — define quais ações cada perfil pode executar em cada fase dos pipelines deste workspace.</span>
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={togglingEnabled}
          onCheckedChange={toggleEnabled}
          aria-label="Habilitar permissões granulares"
        />
      </div>

      {/* ==================== AVISOS DE CONTEXTO (padrão InlineNotice) ==================== */}
      {!enabled ? (
        <InlineNotice variant="info">
          <strong>Regras padrão do CRM:</strong> vendedores veem apenas os próprios negócios;
          supervisores e administradores veem todos. Ative o controle acima para regras por fase e perfil.
        </InlineNotice>
      ) : (
        <InlineNotice variant="warning">
          <strong>Permissões granulares ativas:</strong> supervisores também respeitam a matriz por fase.
          Apenas administradores (root_admin / tenant_admin) mantêm acesso total.
        </InlineNotice>
      )}

      {/* ==================== SELETOR DE PERFIL + MATRIZ ==================== */}
      {enabled && (
        <Card>
          <CardContent className="space-y-3 p-4">
            {loadingProfiles ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando perfis...
              </div>
            ) : profiles.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum perfil de permissão ativo encontrado. Crie perfis em
                Configurações → Perfis de Permissão.
              </p>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1 max-w-sm space-y-1.5">
                  <Label htmlFor={`profile-${workspaceId}`}>Perfil de Permissão</Label>
                  <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                    <SelectTrigger id={`profile-${workspaceId}`} className="w-full">
                      <SelectValue placeholder="Selecione um perfil..." />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Todos os usuários deste perfil receberão estas regras por fase.
                  </p>
                </div>
                {hasChanges && selectedProfileId && (
                  <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salvar Permissões
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ==================== MATRIZ DE PERMISSÕES ==================== */}
      {enabled && selectedProfileId && (
        <>
          {loadingPhases ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Carregando matriz de permissões...
            </div>
          ) : phases.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma fase encontrada. Cadastre pipelines e fases neste workspace primeiro.
              </CardContent>
            </Card>
          ) : (
            <PhasePermissionsTable
              phases={phases}
              groups={groups}
              permissions={permissionsState}
              columns={CRM_PERMISSION_COLUMNS}
              onTogglePermission={togglePermission}
              onToggleColumn={toggleColumn}
              onToggleRow={toggleRow}
              groupLabel="fase"
              description="Marque as ações que este perfil pode realizar em cada fase. Clique no nome da coluna para marcar/desmarcar todas as fases dessa ação."
            />
          )}
        </>
      )}
    </div>
  );
}
